import * as THREE from 'three';
import { hexToWorld } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';

/**
 * The open gate (bet 9, docs/SAGAS.md): the gateway in Brann's ring, where the rampart's runs stop (`rules.openGate`).
 * Two squat stone towers, like the bastion's gatehouse's, and nothing between them, open to the sky (from the table
 * an arch would read as more wall): the two oak leaves stand swung right out against the outer face of the wall, each chained
 * to an iron stake driven into the ground and wedged at the foot with a block of stone, so nobody can shut it. On the
 * threshold, worn flagstones; a red rag of Brann's tied to each tower. Built round the cell's centre
 * with its outer face toward +Z (the rampart's runs line up the same way), one hex wide.
 *
 * Draw calls: one merged mesh (vertex colours). No lights.
 */

const STONE = '#8f887c';
const STONE_DARK = '#6b655c';
const FLAG = '#a19a8d';
const FLAG_DARK = '#867f73';
const OAK = '#5a4232';
const OAK_DARK = '#3e2e24';
const IRON = '#2f3137';
const IRON_EDGE = '#5a5e68';
const RAG = '#b8402c';

export class OpenGateLayer {
  readonly group = new THREE.Group();
  private mat: THREE.MeshStandardMaterial;
  private geo: THREE.BufferGeometry | null = null;

  constructor(ctx: OmenCtx, at: { q: number; r: number }, yaw: number) {
    this.group.name = 'open-gate';
    const c = hexToWorld(at.q, at.r);
    const p = ctx.at(c.x, c.y, 0);
    const g = ctx.ground(p.x, p.z);
    const base = place(p.x, g, p.z, yaw);
    const kit = new Kit();
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
    const tor = new THREE.TorusGeometry(0.5, 0.18, 4, 8);
    // A piece in the gate's own frame (x across the way, z out of the ring), scaled per axis, tilted if asked.
    const at3 = (color: string, geo: THREE.BufferGeometry, x: number, y: number, z: number, sx: number, sy: number, sz: number, ry = 0, rx = 0, rz = 0): void =>
      kit.add(geo, color, base, place(x, y, z, ry, new THREE.Vector3(sx, sy, sz), rx, rz));

    const W = 1.2;
    const D = 0.5;
    // Slim towers at the very edges of the cell, so the opening between them is wide enough to read from the table.
    const tw = 0.24;
    const th = 1.08;
    const tx = W / 2 - tw / 2;
    const openW = W - 2 * tw;
    const doorH = 0.72;
    // The towers, crenellated, each on its own plinth (nothing across the way).
    for (const x of [-tx, tx]) {
      at3(STONE_DARK, box, x, 0.05, 0, tw + 0.08, 0.1, D + 0.1);
      at3(STONE, box, x, th / 2, 0, tw, th, D);
      for (const [mx, mz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) at3(STONE, box, x + mx * (tw / 2 - 0.05), th + 0.06, mz * (D / 2 - 0.05), 0.1, 0.12, 0.1);
      // A course line, darker, so the stone reads as built.
      for (const y of [0.28, 0.56, 0.84]) at3(STONE_DARK, box, x, y, D / 2 + 0.003, tw + 0.004, 0.018, 0.01);
    }
    // The grooves the leaves shut against, empty.
    for (const x of [-openW / 2, openW / 2]) at3(IRON, box, x, doorH / 2, 0.05, 0.03, doorH, 0.04);
    // Pale flagstones through the gate and a stride either side of it, worn and a little uneven: the way in, plain to see.
    for (let i = 0; i < 3; i++)
      for (let j = 0; j < 9; j++) {
        const w = openW + 0.1 - Math.abs(j - 4) * 0.06;
        at3((i + j) % 3 === 0 ? FLAG_DARK : FLAG, box, -w / 2 + w * ((i + 0.5) / 3), 0.012 + ((i * 3 + j) % 3) * 0.004, -1.0 + j * 0.25, w / 3 - 0.03, 0.024, 0.22);
      }
    // The leaves, swung right out and back against the wall's outer face, on their hinges at the towers.
    for (const s of [-1, 1]) {
      const hx = s * (openW / 2);
      const hz = D / 2 + 0.03;
      const lw = openW / 2 + 0.06;
      const lh = doorH - 0.04;
      // Open past square (110° from shut): standing out from the wall, a little back towards its own side.
      const th0 = (110 * Math.PI) / 180;
      const dx0 = -s * Math.cos(th0);
      const dz0 = Math.sin(th0);
      const cx = hx + (dx0 * lw) / 2;
      const cz = hz + (dz0 * lw) / 2;
      const leafYaw = Math.atan2(-dz0, dx0);
      at3(OAK, box, cx, lh / 2 + 0.02, cz, lw, lh, 0.06, leafYaw);
      // Planks and iron bands across it.
      for (const y of [0.16, lh - 0.12]) at3(IRON, box, cx, y, cz, lw + 0.01, 0.045, 0.075, leafYaw);
      for (const k of [-0.33, 0, 0.33]) {
        const dx = Math.cos(leafYaw) * k * lw;
        const dz = -Math.sin(leafYaw) * k * lw;
        at3(OAK_DARK, box, cx + dx, lh / 2 + 0.02, cz + dz, 0.012, lh - 0.02, 0.07, leafYaw);
      }
      // The free end, its iron ring, and the chain down to a stake in the ground.
      const ex = hx + dx0 * lw;
      const ez = hz + dz0 * lw;
      const sx = ex + s * 0.08;
      const sz = ez + 0.32;
      at3(IRON_EDGE, tor, ex, 0.38, ez, 0.07, 0.07, 0.07, leafYaw, Math.PI / 2);
      const links = 5;
      for (let i = 1; i <= links; i++) {
        const t = i / (links + 1);
        const lx = ex + (sx - ex) * t;
        const lz = ez + (sz - ez) * t;
        // It sags a little between the ring and the stake.
        const ly = 0.38 + (0.08 - 0.38) * t - Math.sin(t * Math.PI) * 0.06;
        at3(IRON, tor, lx, ly, lz, 0.045, 0.045, 0.045, Math.atan2(sx - ex, sz - ez) + (i % 2 ? Math.PI / 2 : 0), i % 2 ? 0 : Math.PI / 2);
      }
      at3(IRON, cyl, sx, 0.08, sz, 0.05, 0.22, 0.05, 0, 0.25 * s);
      at3(IRON_EDGE, cyl, sx, 0.19, sz, 0.08, 0.03, 0.08);
      // The wedge at its foot.
      at3(STONE_DARK, box, ex - s * 0.04, 0.06, ez - 0.04, 0.14, 0.12, 0.12, leafYaw + 0.3 * s);
    }
    // Brann's red rag tied to each tower's top, hanging down the outer face.
    for (const x of [-tx, tx]) {
      at3(RAG, box, x, th - 0.12, D / 2 + 0.01, 0.12, 0.26, 0.012, 0, 0, 0.1);
      at3(RAG, box, x + 0.03, th - 0.3, D / 2 + 0.013, 0.08, 0.12, 0.01, 0, 0, -0.16);
    }

    this.mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.08, flatShading: true });
    const mesh = kit.build(this.mat, 'open-gate');
    box.dispose();
    cyl.dispose();
    tor.dispose();
    if (mesh) {
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.geo = mesh.geometry;
      this.group.add(mesh);
    }
    this.group.updateMatrixWorld(true);
  }

  dispose(): void {
    this.geo?.dispose();
    this.mat.dispose();
  }
}
